import { useEffect } from 'react';
import { BrowserRouter, useLocation } from 'react-router-dom';
import { AppRoutes } from './router';
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n';
import { useLanguageInit } from './hooks/useLanguage';
import { CartProvider } from './contexts/CartContext';
import { ProductsProvider } from './contexts/ProductsContext';
import { ToastProvider } from './contexts/ToastContext';
import FloatingCart from './components/feature/FloatingCart';
import CartDrawer from './components/feature/CartDrawer';
import CartDrawerV2 from './components/feature/v2/CartDrawer';
import { useSiteDesign } from './hooks/useSiteDesign';
import './styles/v2-site.css';
import StickyBuyBar from './components/feature/StickyBuyBar';
import { AuthProvider } from './contexts/AuthContext';
import { UserRoleProvider } from './contexts/UserRoleContext';
import { PagePermissionsProvider } from './contexts/PagePermissionsContext';
import { SipPhoneProvider } from './contexts/SipPhoneContext';
import ScrollRestoration from './components/feature/ScrollRestoration';
import PageLoadingBar from './components/feature/PageLoadingBar';
import SiteFavicon from './components/feature/SiteFavicon';
import TrackingScripts from './components/feature/TrackingScripts';
import TrafficTracker from './components/feature/TrafficTracker';
import CookieConsentBanner from './components/feature/CookieConsentBanner';

function AppContent() {
  useLanguageInit();
  const { isNew, ready } = useSiteDesign();
  // Pages without their own new-design version pick up the new look from styles/v2-site.css.
  // Never on the staff console (/admin), which keeps its own look.
  const { pathname } = useLocation();
  const isAdmin = /^\/(ko\/|bn\/)?admin(\/|$)/.test(pathname);
  const skin = isNew && !isAdmin;
  useEffect(() => {
    document.documentElement.classList.toggle('v2-site', skin);
  }, [skin]);

  return (
    <>
      <SiteFavicon />
      <TrackingScripts />
      <TrafficTracker />
      <PageLoadingBar />
      <ScrollRestoration />
      {/* Until the live design is known (first visit only), show a blank page rather than a flash of the wrong design. */}
      {ready || isAdmin ? <AppRoutes /> : <div style={{ minHeight: '100vh', background: '#fff' }} />}
      {/* The new design has its own cart drawer; its header and pages replace the floating cart and sticky bar. */}
      {isNew ? (
        <CartDrawerV2 />
      ) : (
        <>
          <FloatingCart />
          <CartDrawer />
          <StickyBuyBar />
        </>
      )}
      <CookieConsentBanner />
    </>
  );
}

function App() {
  return (
    <I18nextProvider i18n={i18n}>
      <AuthProvider>
        <UserRoleProvider>
          <SipPhoneProvider>
            <PagePermissionsProvider>
              <ProductsProvider>
                <CartProvider>
                  <ToastProvider>
                    <BrowserRouter basename={__BASE_PATH__}>
                      <AppContent />
                    </BrowserRouter>
                  </ToastProvider>
                </CartProvider>
              </ProductsProvider>
            </PagePermissionsProvider>
          </SipPhoneProvider>
        </UserRoleProvider>
      </AuthProvider>
    </I18nextProvider>
  );
}

export default App;